/**
 * OGSAN hero media slider.
 * Crossfades between slides, pauses when off-screen or on a hidden tab,
 * and stays still for visitors who prefer reduced motion.
 */
class OgsanHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-ogsan-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-ogsan-dot]'));
    if (this.slides.length < 2) return;

    this.index = this.slides.findIndex((s) => s.classList.contains('is-active'));
    if (this.index < 0) this.index = 0;

    this.interval = Math.max(2000, parseInt(this.getAttribute('data-interval'), 10) || 5000);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timer = null;
    this.visible = true;

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.show(i);
        this.restart();
      });
    });

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.visible = entry.isIntersecting;
            if (entry.isIntersecting) this.start();
            else this.stop();
          });
        },
        { threshold: 0.15 }
      );
      this.observer.observe(this);
    }

    this.show(this.index);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.observer) this.observer.disconnect();
  }

  show(next) {
    this.index = (next + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');

      // Only let the visible slide's video consume bandwidth.
      const video = slide.querySelector('video');
      if (video) {
        if (active) {
          const played = video.play();
          if (played && typeof played.catch === 'function') played.catch(() => {});
        } else {
          video.pause();
        }
      }
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  start() {
    if (this.reduced || this.timer || document.hidden || !this.visible) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('ogsan-hero-slider')) {
  customElements.define('ogsan-hero-slider', OgsanHeroSlider);
}
